import { useEffect, useRef, useState } from "react";
import { vouchersApi, type BillAlloc, type OpenBill } from "../lib/api";
import { fmtAmount, parseAmount } from "../lib/money";
import { errText } from "../ui/useLoad";

// Bill-wise details for one line: settle open bills of the ledger (oldest first with one key), the rest on account or
// as an advance. Returns the allocations, adding up exactly to the line.
export function BillsPanel({ company, ledgerId, ledgerName, date, amount, value, onChange, onClose, currency, fifoFirst }: {
  company: string; ledgerId: string; ledgerName: string; date: string; amount: number; value: BillAlloc[] | undefined; currency?: string; fifoFirst?: boolean;
  onChange: (b: BillAlloc[] | undefined) => void; onClose: () => void;
}) {
  const [bills, setBills] = useState<OpenBill[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [settle, setSettle] = useState<Record<string, string>>(() =>
    Object.fromEntries((value ?? []).filter((b) => b.kind === "against" && b.ref_name).map((b) => [b.ref_name!, String(b.amount)])));
  const [rest, setRest] = useState<"on_account" | "advance">(() => (value?.some((b) => b.kind === "advance") ? "advance" : "on_account"));
  const [advRef, setAdvRef] = useState(() => value?.find((b) => b.kind === "advance")?.ref_name ?? "");

  useEffect(() => {
    vouchersApi.outstanding(company, ledgerId, date).then(setBills).catch((e) => setErr(errText(e)));
  }, [company, ledgerId, date]);

  // a foreign bill settled in its own currency is counted in that currency
  const open = (b: OpenBill) => (b.currency && b.currency === currency && b.fx_pending != null ? b.fx_pending : b.pending);
  const settled = Object.values(settle).reduce((t, s) => t + (parseAmount(s) || 0), 0);
  const remainder = Math.round((amount - settled) * 100) / 100;

  const fifo = () => {
    let left = amount;
    const next: Record<string, string> = {};
    for (const b of bills ?? []) {
      if (left <= 0) break;
      const take = Math.min(Math.abs(open(b)), left);
      next[b.ref_name] = String(Math.round(take * 100) / 100);
      left -= take;
    }
    setSettle(next);
  };

  // opened from "Set it against open bills": filled oldest first straight away
  const filled = useRef(false);
  useEffect(() => { if (fifoFirst && bills && !filled.current && !value?.length) { filled.current = true; fifo(); } }, [fifoFirst, bills]);   // eslint-disable-line react-hooks/exhaustive-deps

  const apply = () => {
    if (remainder < 0) { setErr("More is settled than the line's amount."); return; }
    const out: BillAlloc[] = Object.entries(settle).filter(([, s]) => parseAmount(s)).map(([ref, s]) => ({ kind: "against", ref_name: ref, amount: parseAmount(s)! }));
    if (remainder > 0 && out.length) out.push(rest === "advance" ? { kind: "advance", ref_name: advRef.trim() || null, amount: remainder } : { kind: "on_account", amount: remainder });
    if (rest === "advance" && remainder > 0 && !advRef.trim()) { setErr("Give the advance a reference."); return; }
    if (!out.length && rest === "advance" && advRef.trim()) out.push({ kind: "advance", ref_name: advRef.trim(), amount });
    onChange(out.length ? out : undefined);
    onClose();
  };

  return (
    <div className="bills-panel" role="dialog" aria-label={`Bills of ${ledgerName}`}
      onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onClose(); } }}>
      <div className="row"><strong>Bills of {ledgerName}</strong><span className="muted small">Line amount {currency ? `${currency} ` : ""}{fmtAmount(amount)}</span></div>
      {err && <p className="msg err">{err}</p>}
      {!bills ? <p className="muted small">Loading open bills…</p> : bills.length === 0 ? <p className="muted small">No open bills.</p> : (
        <table className="grid-table compact">
          <thead><tr><th>Bill</th><th>Date</th><th>Due</th><th className="num">Pending</th><th className="num">Settle</th></tr></thead>
          <tbody>{bills.map((b) => (
            <tr key={b.ref_name}>
              <td>{b.ref_name}{b.kind === "advance" && <span className="tag">advance</span>}</td>
              <td>{b.bill_date ?? ""}</td>
              <td className={b.overdue_days ? "warn" : ""}>{b.due_date ?? ""}{b.overdue_days ? ` (${b.overdue_days}d)` : ""}</td>
              <td className="num">{open(b) !== b.pending && `${b.currency} `}{fmtAmount(Math.abs(open(b)))} {b.pending > 0 ? "Dr" : "Cr"}
                {open(b) !== b.pending && <div className="muted small">₹ {fmtAmount(Math.abs(b.pending))} in the books</div>}</td>
              <td className="num"><input className="num" inputMode="decimal" value={settle[b.ref_name] ?? ""} aria-label={`Settle ${b.ref_name}`}
                onChange={(e) => setSettle({ ...settle, [b.ref_name]: e.target.value })} /></td>
            </tr>))}</tbody>
        </table>
      )}
      <div className="fields inline">
        {bills && bills.length > 0 && <button type="button" onClick={fifo}>Oldest first</button>}
        <span className={`small ${remainder < 0 ? "warn" : "muted"}`}>Left over: {fmtAmount(remainder)}</span>
        <label>as<select value={rest} onChange={(e) => setRest(e.target.value as "on_account" | "advance")}>
          <option value="on_account">On account</option><option value="advance">Advance (new reference)</option></select></label>
        {rest === "advance" && <label>Reference<input value={advRef} onChange={(e) => setAdvRef(e.target.value)} /></label>}
      </div>
      <div className="row"><button type="button" className="primary" onClick={apply}>Done</button>
        <button type="button" className="link" onClick={() => { onChange(undefined); onClose(); }}>Clear bill details</button></div>
    </div>
  );
}
